import { useState } from "react";
import { toast } from "sonner";
import { api, errDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Plane } from "lucide-react";

/* Structured Visa Outcome (2026-09 Premium Admission/Visa Outcome
   correction) — the ONLY frontend surface that calls the canonical
   POST /opportunities/{id}/visa-outcome mechanism. Mirrors
   AdmissionOutcomeActions.jsx's shape exactly: a negative outcome asks
   for the same structured next-action (return for another
   admission-support cycle, or Lost) and never offers any refund/waiver
   control here — a negative visa outcome is never itself a refund event;
   the separate, Leadership-controlled refund/waiver system is reached
   from Customer 360 Finance if a genuinely separate case arises. */
export const VisaOutcomeActions = ({ opportunityId, contactName, customerId, onSaved, hideTriggerButtons = false }) => {
  const [open, setOpen] = useState(false);
  const [outcome, setOutcome] = useState("");
  const [note, setNote] = useState("");
  const [nextAction, setNextAction] = useState("");
  const [lostReason, setLostReason] = useState("");
  const [closureInitiatedBy, setClosureInitiatedBy] = useState("");
  const [saving, setSaving] = useState(false);

  const openDialog = () => {
    setOpen(true); setOutcome(""); setNote("");
    setNextAction(""); setLostReason(""); setClosureInitiatedBy("");
  };

  const reset = () => {
    setOpen(false); setOutcome(""); setNote("");
    setNextAction(""); setLostReason(""); setClosureInitiatedBy("");
  };

  const save = async () => {
    setSaving(true);
    try {
      const r = await api.post(`/opportunities/${opportunityId}/visa-outcome`, {
        outcome,
        note,
        next_action: outcome === "negative" ? nextAction : null,
        lost_reason: outcome === "negative" && nextAction === "lost" ? lostReason : null,
        closure_initiated_by: outcome === "negative" && nextAction === "lost" ? closureInitiatedBy : null,
      });
      const label = outcome === "successful"
        ? "Visa outcome recorded as successful"
        : nextAction === "lost"
          ? "Visa refusal recorded — moved to Lost"
          : "Visa refusal recorded — returned for another admission-support cycle";
      toast.success(r.data?.note || label);
      reset(); onSaved?.();
    } catch (e) { toast.error(errDetail(e)); } finally { setSaving(false); }
  };

  const disabled = saving
    || !outcome
    || !note.trim()
    || (outcome === "negative" && !nextAction)
    || (outcome === "negative" && nextAction === "lost" && !lostReason.trim())
    || (outcome === "negative" && nextAction === "lost" && !closureInitiatedBy);

  return (
    <>
      {!hideTriggerButtons && (
        <Button data-testid={`visa-outcome-trigger-${opportunityId}`} size="sm" variant="outline" className="h-7 text-xs" onClick={openDialog}>
          <Plane className="h-3.5 w-3.5 mr-1" /> Record Visa Outcome
        </Button>
      )}

      <Dialog open={open} onOpenChange={(o) => { if (!o) reset(); }}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader><DialogTitle className="font-heading">Visa outcome</DialogTitle></DialogHeader>
          <div className="space-y-4">
            {(contactName || customerId) && (
              <div className="rounded-md border bg-muted/40 p-2.5 text-xs space-y-0.5">
                <p><b>{contactName}</b>{customerId ? ` · ${customerId}` : ""}</p>
              </div>
            )}

            <div className="space-y-1.5">
              <Label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Visa decision (mandatory)</Label>
              <div className="grid grid-cols-2 gap-1.5">
                {[["successful", "Successful"], ["negative", "Negative"]].map(([v, l]) => (
                  <Button key={v} data-testid={`visa-outcome-${v}-${opportunityId}`} size="sm" variant={outcome === v ? "default" : "outline"} onClick={() => setOutcome(v)}>{l}</Button>
                ))}
              </div>
            </div>

            {outcome === "negative" && (
              <div className="space-y-2 rounded-md border p-2.5">
                <p className="text-[11px] text-muted-foreground">A negative visa outcome does not, by itself, create a refund or waiver — that stays a separate, Leadership-authorised decision if one genuinely applies.</p>
                <Label className="text-xs">What happens next (required)</Label>
                <Select value={nextAction} onValueChange={setNextAction}>
                  <SelectTrigger data-testid="visa-outcome-next-action"><SelectValue placeholder="Choose…" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="university_selection">Return to University Shortlisting / Admission Journey</SelectItem>
                    <SelectItem value="lost">Lost</SelectItem>
                  </SelectContent>
                </Select>
                {nextAction === "lost" && (
                  <>
                    <Input data-testid="visa-outcome-lost-reason" placeholder="Lost reason (required — history preserved)" value={lostReason} onChange={(e) => setLostReason(e.target.value)} />
                    <Select value={closureInitiatedBy} onValueChange={setClosureInitiatedBy}>
                      <SelectTrigger data-testid="visa-outcome-closure-initiated-by"><SelectValue placeholder="Who decided not to proceed? (required)" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="customer">Customer decided not to proceed</SelectItem>
                        <SelectItem value="aussie_pathways">Aussie Pathways declined this customer</SelectItem>
                        <SelectItem value="mutual_other">Mutual / other</SelectItem>
                      </SelectContent>
                    </Select>
                  </>
                )}
              </div>
            )}

            <div className="space-y-1.5">
              <Label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Note (mandatory)</Label>
              <Textarea data-testid="visa-outcome-note-input" placeholder="Note stored with author + timestamp; visible in the visa outcome history…" value={note} onChange={(e) => setNote(e.target.value)} />
            </div>
          </div>
          <DialogFooter>
            <Button data-testid={`visa-outcome-save-${opportunityId}`} disabled={disabled} onClick={save}>
              {saving ? "Saving…" : "Save visa outcome"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};
